import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Truck, Package, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { ordersAPI } from '../services/api';

// Royal Mail shipping options
const shippingOptions = [
  {
    id: 'standard',
    name: 'Royal Mail 2nd Class',
    description: '3-5 working days',
    price: 3.49,
    icon: '📮'
  },
  {
    id: 'first-class',
    name: 'Royal Mail 1st Class',
    description: '1-2 working days',
    price: 4.99,
    icon: '📬'
  },
  {
    id: 'tracked',
    name: 'Royal Mail Tracked 24',
    description: 'Next day delivery (order before 2pm)',
    price: 6.99,
    icon: '🚚'
  },
  {
    id: 'special',
    name: 'Royal Mail Special Delivery',
    description: 'Guaranteed next day by 1pm',
    price: 8.99,
    icon: '⚡'
  }
];

const Checkout = ({ cart, onClearCart, cartCount }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: ''
  });

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const selectedShippingOption = shippingOptions.find(opt => opt.id === selectedShipping);
  const shippingCost = subtotal > 50 ? 0 : selectedShippingOption?.price || 3.49;
  const total = subtotal + shippingCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Prepare order data
      const orderData = {
        items: cart.map(item => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          scent: item.selectedScent || item.scent || '',
          image: item.image || ''
        })),
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postcode: formData.postcode
        },
        shipping: {
          method: selectedShipping,
          methodName: selectedShippingOption?.name || 'Royal Mail 2nd Class',
          price: shippingCost
        },
        subtotal: subtotal,
        shippingCost: shippingCost,
        total: total
      };

      // Create checkout session
      const response = await ordersAPI.createCheckout(orderData);

      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Error',
        description: error.message || 'Failed to process checkout. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar cartCount={cartCount} />

      <div className="container mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Cart</span>
        </button>

        <h1 className="text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Shipping Information */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-serif font-bold text-amber-400 mb-6">Shipping Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                    <Input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                    <Input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                    <Input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                    <Input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Postcode</label>
                    <Input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Truck className="w-6 h-6 text-amber-400" />
                  <h2 className="text-2xl font-serif font-bold text-amber-400">Shipping Method</h2>
                </div>
                
                {subtotal > 50 && (
                  <div className="bg-green-900/30 border border-green-500 rounded-lg p-3 mb-4">
                    <p className="text-green-400 text-sm font-medium">
                      🎉 Congratulations! You qualify for FREE shipping on orders over £50
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {shippingOptions.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedShipping === option.id
                          ? 'border-amber-400 bg-amber-400/10'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shipping"
                        value={option.id}
                        checked={selectedShipping === option.id}
                        onChange={(e) => setSelectedShipping(e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-2xl">{option.icon}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-white">{option.name}</p>
                        <p className="text-sm text-gray-400">{option.description}</p>
                      </div>
                      <div className="text-right">
                        {subtotal > 50 ? (
                          <div>
                            <span className="text-green-400 font-bold">FREE</span>
                            <p className="text-xs text-gray-500 line-through">£{option.price.toFixed(2)}</p>
                          </div>
                        ) : (
                          <span className="font-bold text-amber-400">£{option.price.toFixed(2)}</span>
                        )}
                      </div>
                      {selectedShipping === option.id && (
                        <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </label>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-400">
                    <strong className="text-amber-400">Royal Mail Delivery:</strong> All orders are dispatched within 1-2 business days. 
                    Tracking information will be sent to your email once your order is shipped.
                  </p>
                </div>
              </div>

              {/* Payment Notice */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-6 h-6 text-amber-400" />
                  <h2 className="text-2xl font-serif font-bold text-amber-400">Payment</h2>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-white font-medium">Secure Payment via Stripe</p>
                      <p className="text-sm text-gray-400">You'll be redirected to Stripe's secure checkout page to complete your payment.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-6 bg-white px-2 py-1 rounded" />
                    <span className="text-xs text-gray-500">Accepted: Visa, Mastercard, Amex, Apple Pay, Google Pay</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold py-6 text-lg disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Proceed to Payment - £{total.toFixed(2)}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-serif font-bold text-amber-400 mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cart.map(item => (
                  <div key={`${item.id}-${item.selectedScent || ''}`} className="flex gap-3 pb-4 border-b border-gray-700">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-white">{item.name}</p>
                      {item.selectedScent && <p className="text-xs text-gray-400">{item.selectedScent}</p>}
                      <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-amber-400">
                      £{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal</span>
                  <span className="font-semibold">£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {selectedShippingOption?.name || 'Shipping'}
                  </span>
                  <span className="font-semibold">
                    {shippingCost === 0 ? (
                      <span className="text-green-400">FREE</span>
                    ) : (
                      `£${shippingCost.toFixed(2)}`
                    )}
                  </span>
                </div>
                {subtotal > 50 && (
                  <p className="text-xs text-green-400">Free shipping on orders over £50!</p>
                )}
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between text-xl font-bold text-amber-400">
                    <span>Total</span>
                    <span>£{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;
